import { History } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { formatGBP, formatDateUK, formatStampUK } from "@/lib/api";

const LABELS = {
  created: "Created as draft", updated: "Details changed", submitted: "Submitted for approval",
  approved: "Approved", returned: "Returned for correction", voided: "Voided", payment_choice_reset: "Changed to Payment Pending (payment released)",
  draft_discarded: "Draft discarded", document_attached: "Document attached", document_removed: "Document removed",
  duplicate_exception_requested: "Duplicate exception requested", duplicate_exception_approved: "Duplicate exception approved",
  duplicate_exception_declined: "Duplicate exception declined", duplicate_flag_cleared: "Duplicate flag cleared",
  cross_store_warning_acknowledged: "Other-store warning acknowledged", duplicate_repointed: "Duplicate link moved to the remaining record",
  no_receipt_exception_requested: "No-receipt exception requested", no_receipt_exception_approved: "No-receipt exception approved",
  no_receipt_exception_declined: "No-receipt exception declined", no_receipt_exception_withdrawn: "No-receipt exception withdrawn",
  reversed: "Payment reversed — balances restored", cancelled: "Draft cancelled", created_with_invoice: "Payment recorded with the invoice (Paid in Full)",
  paid_in_full_recorded: "Paid in Full — payment recorded", possible_duplicate_acknowledged: "Possible duplicate acknowledged",
  created_from_purchase_record: "Payment created from the invoice's paid status",
  account_sales_difference_authorised: "Account-sales difference authorised", draft_discarded: "Draft discarded",
  edited: "Details changed", resubmitted: "Corrected and resubmitted",
  auto_approved: "Closed automatically (difference within £5)",
  opening_balance_set: "First-day opening cash set", opening_exception_recorded: "Manager confirmed the opening cash (exception)",
  historical_entry: "Historical date correction",
};
const PAYMENT_LABELS = { submitted: "Payment recorded" };
const ROLES = { employee: "Employee", store_manager: "Manager", accountant: "Accountant", owner: "Owner" };
const MONEY = ["subtotal", "vat_amount", "total_amount", "amount", "amount_paid"];

const fmt = (k, v) => (v === null || v === undefined || v === "" ? "—" : MONEY.includes(k) ? formatGBP(v) : /date/.test(k) ? formatDateUK(v) : String(v));
const stamp = formatStampUK;

function Changes({ ev }) {
  const c = ev.changes || {};
  if (ev.action === "updated") {
    return Object.entries(c).map(([k, v]) => (
      <p key={k} className="text-xs text-navy/60">{k.replace(/_/g, " ")}: <s>{fmt(k, v.old)}</s> → <b>{fmt(k, v.new)}</b></p>
    ));
  }
  if (ev.action === "submitted" && c.ai_extraction_used) {
    const fixes = Object.entries(c.ai_corrections || {});
    return <p className="text-xs text-navy/60">AI-extracted values checked by submitter{fixes.length ? `; corrected: ${fixes.map(([k, v]) => `${k.replace(/_/g, " ")} (${fmt(k, v.ai)} → ${fmt(k, v.final)})`).join(", ")}` : "; no corrections"}</p>;
  }
  if (c.filename) return <p className="text-xs text-navy/60">{c.filename}</p>;
  return null;
}

export default function AuditTimeline({ history, kind }) {
  return (
    <Card className="border-navy/5 shadow-none" data-testid="audit-history">
      <CardContent className="p-4 space-y-3">
        <h3 className="font-semibold text-navy text-sm flex items-center gap-2"><History className="w-4 h-4 text-gold" /> {kind === "payment" ? "Change history" : "Approval & change history"}</h3>
        {!history?.length && <p className="text-xs text-navy/50">No history recorded for this record (created before history tracking began).</p>}
        <ol className="space-y-3 border-l-2 border-gold/30 pl-4">
          {history?.map((ev) => (
            <li key={ev.id} className="relative" data-testid={`audit-event-${ev.id}`}>
              <span className="absolute -left-[21px] top-1.5 w-2.5 h-2.5 rounded-full bg-gold" />
              <p className="text-sm text-navy font-medium">{(kind === "payment" && PAYMENT_LABELS[ev.action]) || LABELS[ev.action] || ev.action}</p>
              <p className="text-xs text-navy/50">{ev.user_name} ({ROLES[ev.user_role] || ev.user_role}) · {stamp(ev.created_at)}</p>
              {ev.reason && <p className="text-xs text-navy/70 mt-0.5">Reason: “{ev.reason}”</p>}
              <Changes ev={ev} />
            </li>
          ))}
        </ol>
      </CardContent>
    </Card>
  );
}
